// The Splits screen and settle up (piece 3 of partner splits). Opened from the Overview Splits row,
// the Requests card and Settings, Partner. Only with an active partner link, except a balance left
// after an unlink, which shows read only. The numbers come from shared/settle.ts, the same maths the
// server uses; the server checks the net again. Settling is one step: the button settles the ticked
// lines at once, and the other person can dispute it for DISPUTE_DAYS.
import type { OverviewDto, SettleCardDto, SettleLineDto, SettlementDto, SettleViewDto } from '../../../shared/api';
import type { SettleLine } from '../../../shared/settle';
import {
  balanceOf,
  disputedNotice,
  HISTORY_SHOWN,
  historyBreakdown,
  historyNetLine,
  historyNetText,
  I_OWED_TITLE,
  markedNotice,
  netLine,
  OWED_TO_ME_TITLE,
  purchasesText,
  REMINDER_TITLE,
  settleButton,
  settlementStatusLine,
  settleSummary,
  SHOW_OLDER,
} from '../../../shared/settle';
import { settleVisible } from '../../../shared/partner';
import { esc, money, shortDate } from '../format';
import { detailBar } from './header';

const signed = (cents: number) => `${cents < 0 ? '-' : '+'}${money(Math.abs(cents), 2)}`;

/** The lines a Settle up would cover now: outstanding and not unticked. */
export function tickedLines(v: SettleViewDto, unticked: ReadonlySet<string>): SettleLineDto[] {
  return v.groups.flatMap((g) => g.lines).filter((l) => !unticked.has(l.id));
}

function lineRow(l: SettleLineDto, unticked: ReadonlySet<string>, canSettle: boolean): string {
  const box = canSettle ? `<input type="checkbox" data-settle-tick="${esc(l.id)}" ${unticked.has(l.id) ? '' : 'checked'} aria-label="Include ${esc(l.merchant)}">` : '';
  return `<label class="srow">${box}<span class="smain"><b>${esc(l.merchant)}</b><small class="muted">${esc(shortDate(l.date))}</small></span><span class="samt ${l.amountCents < 0 ? 'neg' : ''}">${signed(l.amountCents)}</span></label>`;
}

/** "Dispute" (tap twice): only for a settle up my partner marked, within its window. */
const disputeButton = (id: string) => `<button class="btn" data-a="settle-dispute" data-id="${esc(id)}">Dispute</button>`;

/** "Until Oct 16" for a dispute window's end. */
const untilText = (iso: string) => `You can dispute it until ${shortDate(iso.slice(0, 10))}.`;

/** The History section's open entries and whether older ones show (main.ts keeps them in ui; memory only). */
export interface HistoryUi {
  open: ReadonlySet<string>;
  older: boolean;
}

const NO_HISTORY_UI: HistoryUi = { open: new Set(), older: false };

/** One group of an expanded entry: its title (who owed whom, total, count), then each purchase. */
function historyGroup(title: string, lines: SettleLine[]): string {
  if (!lines.length) return '';
  const rows = lines
    .map((l) => `<div class="hline"><span class="hmeta"><span>${esc(l.merchant)}</span><small class="muted">${esc(shortDate(l.date))}</small></span><span class="samt">${money(Math.abs(l.amountCents), 2)}</span></div>`)
    .join('');
  return `<p class="hgrp">${esc(title)}</p>${rows}`;
}

/**
 * One settle up in History: a row that opens across its whole width (chevron, aria-expanded) with
 * the status, the date, "N purchases" and who paid whom in words (historyNetText(): from the settle
 * up's own signed net). Open: the lines in two groups, each with its total, then the net, which is
 * the same words. Dispute stays outside the row, visible while closed.
 */
function historyRow(s: SettlementDto, partner: string, open: boolean): string {
  const marked = shortDate(s.createdAt.slice(0, 10));
  const when = s.disputedAt ? `Marked ${marked}, disputed ${shortDate(s.disputedAt.slice(0, 10))}` : s.view === 'settled' ? `Marked ${marked}` : shortDate((s.respondedAt ?? s.createdAt).slice(0, 10));
  const panelId = `hp-${s.id}`;
  const head = `<button class="hhead" data-a="settle-hist" data-id="${esc(s.id)}" aria-expanded="${open}" aria-controls="${esc(panelId)}"><span class="hmeta"><b>${esc(settlementStatusLine(partner, s))}</b><small class="muted">${esc(
    `${when}, ${purchasesText(s.lines.length)}`,
  )}</small><span class="hwho${s.view === 'settled' ? '' : ' muted'}">${esc(historyNetText(partner, s))}</span></span><span class="chev" aria-hidden="true">${open ? '&#9662;' : '&#9656;'}</span></button>`;
  const act = s.canDispute ? `<div class="hact"><small class="muted">${esc(untilText(s.disputeUntil))}</small><span class="btns">${disputeButton(s.id)}</span></div>` : '';
  let panel = '';
  if (open) {
    const b = historyBreakdown(s.lines);
    panel = `<div class="hpanel" id="${esc(panelId)}">${historyGroup(OWED_TO_ME_TITLE(partner, b.owedToMe.totalCents, b.owedToMe.lines.length), b.owedToMe.lines)}${historyGroup(
      I_OWED_TITLE(partner, b.iOwed.totalCents, b.iOwed.lines.length),
      b.iOwed.lines,
    )}<p class="hnet">${esc(historyNetLine(partner, s))}</p></div>`;
  }
  return `<div class="hentry">${head}${act}${panel}</div>`;
}

/** History: the HISTORY_SHOWN newest settle ups, then "Show older" for the rest. */
function history(v: SettleViewDto, h: HistoryUi): string {
  if (!v.history.length) return '';
  const shown = h.older ? v.history : v.history.slice(0, HISTORY_SHOWN);
  const more = v.history.length - shown.length;
  return `<h2>History</h2><div class="list">${shown.map((s) => historyRow(s, v.partnerName, h.open.has(s.id))).join('')}</div>${
    more ? `<div class="btns"><button class="btn" data-a="settle-older">${esc(SHOW_OLDER(more))}</button></div>` : ''
  }`;
}

export function vSettle(v: SettleViewDto | null, unticked: ReadonlySet<string>, h: HistoryUi = NO_HISTORY_UI): string {
  const head = detailBar('Splits', v ? `With ${v.partnerName}` : '');
  if (!v) return `${head}<p class="empty">Loading...</p>`;
  const p = v.partnerName;
  const ticked = tickedLines(v, unticked);
  const t = balanceOf(ticked);
  const top = `<section class="hero"><p class="lab">Balance</p><p class="big">${esc(netLine(p, v.balance.netCents))}</p>
  <p class="sub">${esc(p)} owes you ${money(v.balance.owedToMeCents, 2)}. You owe ${esc(p)} ${money(v.balance.iOweCents, 2)}.</p>
  ${v.canSettle ? '' : `<p class="hint">You are no longer linked with ${esc(p)}. The balance stays here to see; settling needs a link.</p>`}</section>`;
  const groups = v.groups.length
    ? v.groups
        .map(
          (g) => `<h3 class="sgroup"><span>${esc(g.categoryName)}</span><span>${signed(g.subtotalCents)}</span></h3>
  <div class="list">${g.lines.map((l) => lineRow(l, unticked, v.canSettle)).join('')}</div>`,
        )
        .join('')
    : '<p class="hint">Nothing to settle. Accepted splits show here until they are settled.</p>';
  const settleBtn =
    v.canSettle && v.groups.length
      ? `<p class="hint">${ticked.length ? `${esc(settleSummary(p, t.netCents, ticked.length))} Pay each other however you like. Settling does not change either budget.` : 'Tick the splits to settle. Unticked ones stay for next time.'}</p>
  <div class="btns"><button class="btn primary" data-a="settle-send" data-net="${t.netCents}" ${ticked.length ? '' : 'disabled'}>${esc(ticked.length ? settleButton(p, t.netCents) : 'Settle up')}</button></div>`
      : '';
  const requests = v.requests.length
    ? `<h2>Not counted</h2><p class="hint">Waiting or declined requests never count toward the balance.</p><div class="list">${v.requests
        .map(
          (r) =>
            `<div class="srow"><span class="smain"><b>${esc(r.merchant)}</b><small class="muted">${esc(shortDate(r.date))}, ${r.status === 'pending' ? 'waiting' : 'declined'}</small></span><span class="samt muted">${signed(r.amountCents)}</span></div>`,
        )
        .join('')}</div>`
    : '';
  return `${head}${top}<h2>Outstanding</h2>${groups}${settleBtn}${requests}${history(v, h)}`;
}

/** "2 requests": split requests from my partner waiting for my answer (the Requests card has them). */
export const requestsBadge = (n: number) => (n ? `${n} request${n === 1 ? '' : 's'}` : '');

/**
 * The right side of the Splits row: the net, "All settled" (nothing outstanding, nothing waiting),
 * or "All square" (zero, but lines that even out or something still waiting). Null while the
 * Splits screen's data loads.
 */
export function splitsRowNet(name: string, v: SettleViewDto | null, card: SettleCardDto | null): { text: string; quiet: boolean } | null {
  const net = v ? v.balance.netCents : card ? card.netCents : null;
  if (net === null) return null;
  if (net) return { text: netLine(name, net), quiet: false };
  // Nothing pending: no outstanding lines and no request waiting either way.
  const nothing = v ? !v.groups.length && !v.requests.some((r) => r.status === 'pending') : !card?.remind;
  return nothing ? { text: 'All settled', quiet: true } : { text: 'All square', quiet: false };
}

/**
 * The Splits row on Overview (below New purchases and Requests, above the groups): one tappable line
 * to the Splits screen, "Splits with Tiffany" and the net. Only with an active link, or after an
 * unlink while a balance is left. Stays at $0 ("All settled"). Its height is fixed, so the net
 * arriving (GET /api/settle, loaded after the overview) never moves the page; until then a quiet
 * placeholder shows. Requests waiting for me add a count. A settle up my partner marked (with
 * Dispute, for DISPUTE_DAYS) or disputed, or the last day and the 1st with something outstanding
 * (the overview's `settle`), turn the same row into a card, in the same place: never a second card.
 */
export function vSplitsRow(d: OverviewDto, v: SettleViewDto | null): string {
  if (!settleVisible(d.partner) || d.isSample) return '';
  const name = d.partner.linked?.name ?? v?.partnerName ?? null;
  const card = d.settle;
  const net = splitsRowNet(name ?? 'Your partner', v, card);
  const badge = d.partner.linked ? requestsBadge(d.splitRequests.length) : '';
  const right = net
    ? `<span class="snet${net.quiet ? ' muted' : ''}">${esc(net.text)}</span>`
    : '<span class="snet ph" aria-hidden="true"></span>';
  const row = `<button class="splitsrow" data-a="settle-screen"${net ? '' : ' aria-busy="true"'}><span class="sname"><span class="snm">${esc(name ? `Splits with ${name}` : 'Splits')}</span>${
    badge ? ` <span class="rbadge">${esc(badge)}</span>` : ''
  }</span>${right}</button>`;
  if (!card) return `<section class="splits" data-splits>${row}</section>`;
  const n = card.notice;
  const parts: string[] = [];
  if (n?.kind === 'marked') {
    const m = markedNotice(card.partnerName, n.netCents);
    parts.push(`<p><b>${esc(m.line)}</b></p><p class="hint">${esc(m.who)} ${esc(untilText(n.until))}</p><div class="btns">${disputeButton(n.settlementId)}</div>`);
  } else if (n?.kind === 'disputed') parts.push(`<p class="hint">${esc(disputedNotice(card.partnerName))}</p>`);
  if (card.remind) parts.push('<p class="hint">It is the end of the month: a good time to settle up.</p>');
  return `<section class="ask settlecard" data-splits><h2>${esc(n ? 'Settle up' : REMINDER_TITLE)}</h2>${row}${parts.join('')}</section>`;
}

/** Settings, Partner and the Requests card: the way in, while the screen exists. */
export const settleLink = (d: OverviewDto) =>
  settleVisible(d.partner) && !d.isSample ? `<div class="btns"><button class="btn" data-a="settle-screen">Splits and settle up</button></div>` : '';
